/**
 * useHardwareStatus - Poll le Hardware Bridge pour connaitre l'etat des peripheriques
 * Reutilise le pattern de OfflineIndicator.tsx (fetch /health avec fallback HTTPS/HTTP)
 */
import { useState, useEffect, useCallback } from 'react';
import { EN_TETE_BRIDGE } from '../services/bridgeHeaders';

interface HardwareStatus {
  bridgeConnected: boolean;
  scaleConnected: boolean;
  epsonAvailable: boolean;
  zebraAvailable: boolean;
  posId: string | null;
  // Réseau de la machine. Le navigateur ne peut pas lire le SSID (aucune API
  // ne l'expose) — c'est le bridge, qui tourne sur le Mac mini, qui le fournit.
  // Utile au marché : voir sur quel Wi-Fi la caisse est réellement branchée.
  ssid: string | null;
  networkLabel: string | null;
  ip: string | null;
  rssi: number | null;
  /** Jours restants avant expiration du certificat du bridge (null si inconnu) */
  certDaysLeft: number | null;
  /** Date d'expiration du certificat du bridge, ISO (null si inconnue) */
  certExpiresAt: string | null;
  /** Le bridge de CETTE machine sait ouvrir l'app Musique (POS-01/02 seulement) */
  musicAvailable: boolean;
}

const BRIDGE_BASE_URLS = [
  'https://localhost:3334',
];

const POLL_INTERVAL = 30000; // 30 secondes

export function useHardwareStatus() {
  const [status, setStatus] = useState<HardwareStatus>({
    bridgeConnected: false,
    scaleConnected: false,
    epsonAvailable: false,
    zebraAvailable: false,
    posId: null,
    ssid: null,
    networkLabel: null,
    ip: null,
    rssi: null,
    certDaysLeft: null,
    certExpiresAt: null,
    musicAvailable: false,
  });

  const fetchStatus = useCallback(async () => {
    for (const baseUrl of BRIDGE_BASE_URLS) {
      try {
        const response = await fetch(`${baseUrl}/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(2000),
        });

        if (response.ok) {
          const data = await response.json();
          setStatus({
            bridgeConnected: true,
            scaleConnected: data.hardware?.scale?.connected ?? false,
            epsonAvailable: data.hardware?.epson?.available ?? false,
            zebraAvailable: data.hardware?.zebra?.available ?? false,
            posId: data.posId ?? null,
            ssid: data.network?.ssid ?? null,
            networkLabel: data.network?.label ?? data.network?.ssid ?? null,
            ip: data.network?.ip ?? null,
            rssi: data.network?.rssi ?? null,
            certDaysLeft: data.certificate?.daysLeft ?? null,
            certExpiresAt: data.certificate?.expiresAt ?? null,
            musicAvailable: data.features?.music === true,
          });
          return;
        }
      } catch {
        // Essayer l'URL suivante
      }
    }

    // Aucune URL n'a repondu
    setStatus({
      bridgeConnected: false,
      scaleConnected: false,
      epsonAvailable: false,
      zebraAvailable: false,
      posId: null,
      ssid: null,
      networkLabel: null,
      ip: null,
      rssi: null,
    certDaysLeft: null,
    certExpiresAt: null,
    musicAvailable: false,
    });
  }, []);

  const restartBridge = useCallback(async (): Promise<boolean> => {
    for (const baseUrl of BRIDGE_BASE_URLS) {
      try {
        const response = await fetch(`${baseUrl}/restart`, {
          method: 'POST',
          // Sans cet en-tête le bridge refuse (403) : c'est lui qui empêche une
          // page web quelconque ouverte sur le Mac d'éteindre la caisse.
          headers: EN_TETE_BRIDGE,
          signal: AbortSignal.timeout(3000),
        });
        if (response.ok) {
          // Attendre que le bridge redémarre puis re-poll
          setTimeout(fetchStatus, 3000);
          return true;
        }
      } catch {
        // Essayer l'URL suivante
      }
    }
    return false;
  }, [fetchStatus]);

  // Extinction du Mac mini (fin de journée) — le bridge exécute l'arrêt macOS.
  const shutdownPos = useCallback(async (): Promise<boolean> => {
    for (const baseUrl of BRIDGE_BASE_URLS) {
      try {
        const response = await fetch(`${baseUrl}/shutdown`, {
          method: 'POST',
          // Sans cet en-tête le bridge refuse (403) : c'est lui qui empêche une
          // page web quelconque ouverte sur le Mac d'éteindre la caisse.
          headers: EN_TETE_BRIDGE,
          signal: AbortSignal.timeout(3000),
        });
        if (response.ok) return true;
      } catch {
        // Essayer l'URL suivante
      }
    }
    return false;
  }, []);

  // Ouvrir l'app Musique (Apple Music) — le bridge lance `open -a Music`.
  const openMusic = useCallback(async (): Promise<boolean> => {
    for (const baseUrl of BRIDGE_BASE_URLS) {
      try {
        const response = await fetch(`${baseUrl}/music/open`, {
          method: 'POST',
          headers: EN_TETE_BRIDGE,
          signal: AbortSignal.timeout(5000),
        });
        if (response.ok) return true;
      } catch {
        // Essayer l'URL suivante
      }
    }
    return false;
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return { ...status, restartBridge, shutdownPos, openMusic, refreshStatus: fetchStatus };
}
